<template>
  <div class="display-change-control">
    <div class="grid-x align-middle">
      <div class="cell auto">
        <h3 v-if="title" class="filter-title">
          {{ title }}
        </h3>
      </div>

      <div class="cell shrink filter-control">
        <button
          type="button"
          class="filter-trigger"
          :aria-expanded="isOpen.toString()"
          aria-label="Toggle filters"
          @click="togglePanel"
        >
          <InlineSvg
            :src="icons.filter"
            :width="24"
            :height="24"
          />
        </button>
      </div>
    </div>

    <transition name="fade-slide">
      <div
        v-if="isOpen"
        class="filter-panel"
      >
        <div class="filter-panel-items">
          <div
            v-for="control in controls"
            :key="control.key"
            class="filter-panel-item"
          >
            <label
              :for="getControlId(control)"
              class="filter-label"
            >
              <span class="filter-label-text">
                {{ control.props.label }}
              </span>

              <select
                v-if="control.type === 'select'"
                :id="getControlId(control)"
                v-model="filters[control.key]"
                class="filter-field"
              >
                <option
                  v-for="option in control.props.options"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </select>

              <input
                v-else-if="control.type === 'checkbox'"
                :id="getControlId(control)"
                v-model="filters[control.key]"
                class="filter-checkbox"
                type="checkbox"
              />

              <input
                v-else-if="control.type === 'text'"
                :id="getControlId(control)"
                v-model="filters[control.key]"
                class="filter-field"
                type="text"
                :placeholder="control.props.placeholder ?? ''"
              />

              <span
                v-else
                class="unsupported-control"
              >
                Unsupported control
              </span>
            </label>
          </div>
        </div>

        <div
          v-if="showButtons"
          class="filter-panel-buttons"
        >
          <button
            type="button"
            class="filter-reset"
            @click="resetFilters"
          >
            Reset
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { reactive, ref, watch } from 'vue'
import { icons } from '@/assets/icons'
import InlineSvg from 'vue-inline-svg'

const props = defineProps({
  title: {
    type: String,
    default: 'Filters'
  },

  controls: {
    type: Array,
    default: () => []
  },

  showButtons: {
    type: Boolean,
    default: true
  },

  initiallyOpen: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:filters'])

const isOpen = ref(props.initiallyOpen)
const filters = reactive({})

function getDefaultValue(control) {
  if (control.default !== undefined) {
    return control.default
  }

  return control.type === 'checkbox'
    ? false
    : ''
}

function initialiseFilters(controls) {
  controls.forEach((control) => {
    if (!(control.key in filters)) {
      filters[control.key] = getDefaultValue(control)
    }
  })

  Object.keys(filters).forEach((key) => {
    const controlStillExists = controls.some(
      (control) => control.key === key
    )

    if (!controlStillExists) {
      delete filters[key]
    }
  })
}

function getControlId(control) {
  return `filter-${String(control.key).toLowerCase()}`
}

function togglePanel() {
  isOpen.value = !isOpen.value
}

function resetFilters() {
  props.controls.forEach((control) => {
    filters[control.key] = getDefaultValue(control)
  })
}

initialiseFilters(props.controls)

watch(
  () => props.controls,
  (controls) => {
    initialiseFilters(controls)
  },
  {
    deep: true
  }
)

watch(
  filters,
  (newValue) => {
    emit('update:filters', { ...newValue })
  },
  {
    deep: true
  }
)
</script>

<style scoped>
.display-change-control {
  width: 100%;
}

.filter-title {
  margin: 0;
}

/* Trigger row */
.filter-control {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.filter-trigger {
  width: 34px;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  padding: 0;
  border: 0;
  border-radius: 999px;

  background: #f5f8fc;
  color: inherit;

  cursor: pointer;
  transition:
    background 0.15s ease,
    box-shadow 0.15s ease;
}

.filter-trigger:hover {
  background: #eef4fb;
}

.filter-trigger:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.18);
}

/* Full-width panel */
.filter-panel {
  width: 100%;
  margin-top: 10px;
  padding: 24px;

  border-radius: 12px;
  background: var(--palepink);
}

/* Three equal columns on desktop */
.filter-panel-items {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 24px;
  width: 100%;
}

.filter-panel-item {
  min-width: 0;
}

/* Label and field spacing */
.filter-label {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 10px;

  margin: 0;

  text-align: left;
  font-size: 18px;
  font-weight: 800;
  color: #102a43;
}

.filter-label-text {
  display: block;
  line-height: 1.3;
}

/* Shared fields */
.filter-field {
  width: 100%;
  min-width: 0;
  height: 44px;

  margin: 0;
  padding: 0 12px;

  font-size: 14px;
  font-weight: 400;
  color: #0f172a;

  border: 1px solid #d6e4f3;
  border-radius: 10px;
  background: #ffffff;

  outline: none;

  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;
}

.filter-field:hover {
  border-color: #c3d7ee;
}

.filter-field:focus {
  border-color: #60a5fa;
  box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.22);
}

/* Checkbox */
.filter-checkbox {
  width: 18px;
  height: 18px;
  margin: 0;
}

/* Reset */
.filter-panel-buttons {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
}

.filter-reset {
  padding: 6px 10px;

  border: 0;
  border-radius: 10px;
  background: transparent;

  font-size: 14px;
  font-weight: 600;
  color: #1d4ed8;

  cursor: pointer;
  transition: background 0.15s ease;
}

.filter-reset:hover {
  background: rgba(29, 78, 216, 0.08);
}

.filter-reset:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.18);
}

.unsupported-control {
  font-size: 14px;
  font-weight: 400;
  color: #b91c1c;
}

/* Transition */
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition:
    opacity 0.18s ease,
    transform 0.18s ease;
}

.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

/* Tablet */
@media screen and (max-width: 63.9375em) {
  .filter-panel-items {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

/* Mobile */
@media screen and (max-width: 39.9375em) {
  .filter-panel {
    padding: 16px;
  }

  .filter-panel-items {
    grid-template-columns: 1fr;
    gap: 18px;
  }
}
</style>